import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LogOut, UserCircle2, ShieldCheck, ChevronDown, LayoutDashboard,
  Briefcase, Users2, Gavel, ScrollText, ClipboardList,
  FileText, Wallet, FolderArchive, CreditCard, MessageSquare,
} from "lucide-react";

// Todos los módulos en una sola fila con scroll horizontal si excede el ancho
const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/despacho", label: "Despacho", Icon: Briefcase },
  { to: "/clientes", label: "Clientes", Icon: Users2 },
  { to: "/procesos", label: "Procesos", Icon: Gavel },
  { to: "/notarial", label: "Notarial", Icon: ScrollText },
  { to: "/solicitudes", label: "Solicitudes", Icon: ClipboardList },
  { to: "/mensajes", label: "Mensajes", Icon: MessageSquare },
  { to: "/formularios", label: "Formularios", Icon: FileText },
  { to: "/cobros", label: "Cobros", Icon: Wallet },
  { to: "/archivo", label: "Archivo", Icon: FolderArchive },
  { to: "/pagos", label: "Suscripción", Icon: CreditCard, superAdminOnly: true },
];

function NavItem({ to, label, Icon, isSuper }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-full font-medium whitespace-nowrap transition-all ${
          isActive
            ? "bg-[#7A1E2E] text-[#F1EDE4] shadow-[0_4px_14px_-4px_rgba(122,30,46,0.5)]"
            : "text-[#3d2a2a] hover:bg-white hover:text-[#7A1E2E]"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.8} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuper = user && user.role === "super_admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header
      className="sticky top-0 z-40 bg-[#F1EDE4]/90 backdrop-blur-md border-b border-[#E5DFD3]"
      data-testid="top-nav"
    >
      {/* Brand row */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-8 flex items-center justify-between h-20">
        <NavLink to="/dashboard" data-testid="nav-logo-link">
          <Logo variant="dark" size="md" />
        </NavLink>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white transition-colors"
            >
              <div className="hidden md:block text-right leading-tight">
                <div className="text-sm text-[#1A0810] font-medium">{user?.name || user?.email}</div>
                <div className="text-[11px] text-[#6B5D57]">{user?.email}</div>
              </div>
              <Avatar className="h-10 w-10 ring-1 ring-[#E5DFD3]">
                {user?.avatar_url ? <AvatarImage src={user.avatar_url} alt={user?.name} /> : null}
                <AvatarFallback className="bg-[#7A1E2E] text-[#F1EDE4] text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <ChevronDown className="h-4 w-4 text-[#6B5D57]" strokeWidth={1.6} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 bg-white border-[#E5DFD3] text-[#1A0810]">
            <DropdownMenuLabel className="text-[#6B5D57]">
              <div className="text-[10px] uppercase tracking-widest text-[#C25A5A] font-semibold">
                {user?.role === "super_admin" ? "Super Administrador" : user?.role === "admin" ? "Administrador" : "Usuario"}
              </div>
              <div className="mt-1 text-sm text-[#1A0810] font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#E5DFD3]" />
            <DropdownMenuItem
              data-testid="menu-profile"
              onClick={() => navigate("/despacho")}
              className="cursor-pointer focus:bg-[#F5EEE0]"
            >
              <UserCircle2 className="mr-2 h-4 w-4" strokeWidth={1.6} />
              Despacho
            </DropdownMenuItem>
            {isSuper && (
              <DropdownMenuItem
                data-testid="menu-super-admin"
                onClick={() => navigate("/pagos")}
                className="cursor-pointer focus:bg-[#F5EEE0]"
              >
                <ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.6} />
                Super Administrador
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[#E5DFD3]" />
            <DropdownMenuItem
              data-testid="menu-logout"
              onClick={handleLogout}
              className="cursor-pointer text-[#7A1E2E] focus:bg-[#FBEDEF]"
            >
              <LogOut className="mr-2 h-4 w-4" strokeWidth={1.6} />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Nav — una sola fila */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-8 pb-4 overflow-x-auto">
        <div className="flex items-center gap-1.5 min-w-max">
          {NAV_ITEMS.filter((n) => !n.superAdminOnly || isSuper).map((n) => (
            <NavItem key={n.to} {...n} />
          ))}
        </div>
      </div>
    </header>
  );
}
